import { Link } from "@tanstack/react-router"
import { useTheme } from "../components/theme"
import { Sun, Moon } from "lucide-react"
import { Button } from "../components/ui"

const LAST_UPDATED = "June 28, 2025"

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  )
}

export default function TermsPage() {
  const { theme, toggle } = useTheme()

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b bg-card/80 px-4 py-3 shadow-sm backdrop-blur sm:px-6">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/landing" className="flex items-center gap-2">
            <img src="/logo.svg" alt="Taskoru" className="h-9 w-9 rounded-xl shadow-sm" />
            <span className="text-lg font-semibold tracking-tight">Taskoru</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="sm" onClick={toggle} title={theme === "dark" ? "Light mode" : "Dark mode"}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Link to="/login" className="hidden text-sm font-medium text-muted-foreground hover:text-foreground sm:inline">Log in</Link>
            <Link to="/register"><Button size="sm">Get started</Button></Link>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-12 sm:px-6">
        <div className="mb-10">
          <h1 className="mb-2 text-3xl font-bold tracking-tight">Terms of Service</h1>
          <p className="text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="space-y-10">
          <Section title="The short version">
            <p>Use Taskoru to get work done. Don't use it to harm people or break the law. Pay for what you use. We'll do our best to keep things running reliably. If something goes wrong, our liability is limited to what you've paid us in the past 3 months.</p>
          </Section>

          <Section title="Who can use Taskoru">
            <p>You need to be at least 13 years old. By using Taskoru you confirm you have the legal authority to agree to these terms — either for yourself or on behalf of an organisation.</p>
          </Section>

          <Section title="Your account">
            <p>You're responsible for keeping your login credentials secure. Don't share your password. If you suspect unauthorised access to your account, let us know immediately.</p>
            <p>One person per account. You can invite as many team members as your plan allows — each one needs their own account.</p>
          </Section>

          <Section title="Your content">
            <p>You own everything you create in Taskoru. We don't claim any rights to your boards, cards, files, or comments.</p>
            <p>By storing content on Taskoru you give us permission to host it and display it back to you and the people you invite. That's the only licence you're granting us.</p>
            <p>You're responsible for making sure your content doesn't violate anyone else's rights or any applicable laws.</p>
          </Section>

          <Section title="What you can't do">
            <p>A short list of things that will get your account terminated:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>Using Taskoru to harass, threaten, or harm others</li>
              <li>Attempting to access accounts or data that isn't yours</li>
              <li>Scraping, reverse-engineering, or overloading our infrastructure</li>
              <li>Using Taskoru to store or distribute illegal content</li>
              <li>Reselling or sublicensing Taskoru without our written permission</li>
            </ul>
          </Section>

          <Section title="Plans and billing">
            <p>The Free plan is free forever. No credit card required.</p>
            <p>Pro and Business plans are billed monthly or annually via Stripe. Prices are shown on the pricing page and don't change mid-subscription.</p>
            <p><strong className="text-foreground">Cancellations.</strong> You can cancel any time. Your plan stays active until the end of the current billing period, then drops to Free. We don't offer partial-month refunds, but if something went genuinely wrong contact us and we'll sort it out.</p>
            <p><strong className="text-foreground">Plan changes.</strong> Upgrades take effect immediately. Downgrades take effect at the next billing cycle.</p>
          </Section>

          <Section title="Uptime and reliability">
            <p>We aim to keep Taskoru available and fast. We run on Cloudflare's global infrastructure which is genuinely reliable, but we can't promise 100% uptime. Planned maintenance will be communicated in advance where possible.</p>
            <p>We don't offer an SLA on the Free plan. Pro and Business users can reach support for priority assistance.</p>
          </Section>

          <Section title="Our liability">
            <p>Taskoru is provided as-is. We're not liable for any indirect, incidental, or consequential damages arising from your use of the service.</p>
            <p>If we do cause direct damage, our total liability is capped at the amount you paid us in the 3 months before the incident. If you're on the Free plan that cap is $0 — which is why we'd encourage you to keep backups of anything critical.</p>
          </Section>

          <Section title="Termination">
            <p><strong className="text-foreground">By you.</strong> You can delete your account at any time from your profile settings or by contacting us.</p>
            <p><strong className="text-foreground">By us.</strong> We can suspend or terminate accounts that violate these terms, with notice where reasonable. For serious violations (illegal activity, abuse) we may terminate immediately.</p>
            <p>When an account is deleted, your data is removed within 30 days as described in our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.</p>
          </Section>

          <Section title="Changes to these terms">
            <p>We'll update these terms when we need to. If the changes are material we'll email you at least 14 days before they take effect. Continuing to use Taskoru after that means you accept the updated terms.</p>
          </Section>

          <Section title="Governing law">
            <p>These terms are governed by the laws of the jurisdiction where Taskoru operates. Any disputes will be resolved in the courts of that jurisdiction.</p>
          </Section>

          <Section title="Contact">
            <p>If you have questions about these terms, reach us through the <a href="/landing#contact" className="text-primary hover:underline">contact form</a> on our site. We'll respond promptly.</p>
          </Section>
        </div>
      </main>

      <footer className="border-t bg-card px-4 py-8 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Taskoru" className="h-7 w-7 rounded-xl" />
            <span className="font-semibold">Taskoru</span>
          </div>
          <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} Taskoru. All rights reserved.</p>
          <div className="flex gap-4 text-sm text-muted-foreground">
            <a href="/landing#contact" className="hover:text-foreground">Contact</a>
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="text-foreground font-medium">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
